/*
 * Singurul loc care citeste import.meta.env.
 *
 * Restul aplicatiei importa `config`, nu variabile de mediu. Asa exista un
 * singur loc unde se valideaza si se dau valorile implicite, si un singur loc
 * de schimbat daca maine configurarea vine din alta parte.
 */

function readNumber(raw: string | undefined, fallback: number, max?: number): number {
  const parsed = Number(raw);
  // NaN si 0 cad amandoua pe implicit: un timeout de 0 ar anula orice cerere
  // instant, iar o pagina de 0 elemente ar arata un catalog gol si corect.
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return max === undefined ? parsed : Math.min(parsed, max);
}

export const config = {
  /* Bara finala se taie aici, o data, ca sa nu existe niciodata "//shop" in cai. */
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, ''),

  requestTimeoutMs: readNumber(import.meta.env.VITE_API_TIMEOUT_MS, 20_000),

  /* Contractul taie per_page peste 100 fara eroare; se taie si aici, ca numarul
     cerut si numarul primit sa fie acelasi si paginarea sa iasa corect. */
  pageSize: readNumber(import.meta.env.VITE_PAGE_SIZE, 24, 100),

  /*
   * Unde se deschide un tichet in platforma. Sablon cu `{id}` in el.
   *
   * Aprobarea nu mai e in magazin, ci pe tichet, deci ecranul cererii trebuie
   * sa duca acolo. Magazinul e insa o aplicatie separata si nu are de unde sti
   * singur hostul portalului, iar contractul nu are ruta care sa i-l spuna.
   * Gol inseamna "nu se stie": numerele de tichet se scriu ca text, fara link.
   * Un link ghicit ar duce in 404 si ar arata ca un tichet care nu exista.
   */
  ticketUrlTemplate: (import.meta.env.VITE_TICKET_URL ?? '').trim(),

  /*
   * Politica de confidentialitate.
   *
   * Nu e decorativa: si Google Play, si App Store cer ca legatura sa fie
   * accesibila DIN aplicatie, nu doar in fisa din magazin. Fara ea, publicarea
   * poate fi respinsa.
   *
   * Goala implicit, si asta e intentionat: adresa se alege in functie de limba
   * din interfata, prin `privacyUrl()` din lib/privacy.ts. Aici ramane doar
   * suprascrierea, pentru instalarile care au politica lor gazduita altundeva -
   * atunci se pune adresa completa si aceea se foloseste in ambele limbi.
   */
  privacyUrl: (import.meta.env.VITE_PRIVACY_URL ?? '').trim(),

  /* sessionStorage, nu localStorage: statie comuna intr-o banca, tokenul
     trebuie sa moara odata cu fila. */
  sessionStorageKey: 'catalog.session',

  /*
   * Refresh tokenul in cookie httpOnly, nu in pagina. Numai pe web.
   *
   * Stins implicit, si asa ramane pana cand api-ul (ph4, /shop/auth/*) stie sa
   * puna si sa citeasca cookie-ul: aprins, login-ul, schimbul SSO, refresh-ul si
   * logout-ul pleaca cu `credentials: 'include'` si cu
   * `refresh_transport: 'cookie'` in corp. Un api care nu-l stie raspunde ca
   * pana acum (refresh_token in corp), iar clientul merge pe drumul vechi - dar
   * cererea cu credentiale cere CORS cu origine exacta, nu "*", de-asta nu se
   * aprinde pe ghicite. VITE_REFRESH_COOKIE=1 la build.
   */
  refreshCookie: (import.meta.env.VITE_REFRESH_COOKIE ?? '').trim() === '1',
} as const;
